import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'
import { getAuthToken, unpackAuthToken } from '~/helpers/sessionHelpers'
import { useTRPC } from '~/services/trpc'

import { redirectToLogin, removeSessionToken, setSessionToken } from './utils'

// Every hour
const SESSION_REFRESH_INTERVAL_MS = 1 * 60 * 60 * 1000
const SESSION_REFRESH_FOCUS_COOLDOWN_MS = 30 * 60 * 1000

export function useAuthenticator() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const mutator = useMutation(trpc.session.grant.mutationOptions())

  const redirect = useCallback(
    (orgId: string) => {
      navigate({
        to: '/org/$orgId/$',
        params: { orgId },
        reloadDocument: true,
        replace: true, // This replaces history entry like location.replace()
      })
    },
    [navigate],
  )

  const authenticate = useCallback(
    async (input: {
      organizationId: string
      legacyId: string
      default: boolean
    }): Promise<void> => {
      const session = unpackAuthToken(getAuthToken())
      if (session && session.oui && session.oui[input.legacyId]) {
        console.log('already authenticated... redirecting now')
        redirect(input.legacyId)
      } else {
        const { token } = await mutator.mutateAsync({
          organizationId: input.legacyId,
        })
        setSessionToken(token)
        redirect(input.legacyId)
      }
    },
    [],
  )

  return [authenticate, mutator.isPending] as const
}

export function useSessionRefresher() {
  const trpc = useTRPC()
  const refreshMutator = useMutation(trpc.session.refresh.mutationOptions())
  const inFlightRef = useRef(false)
  const lastRefreshAtRef = useRef<number>(0)

  const refreshSession = useCallback(async () => {
    if (inFlightRef.current) return
    if (!getAuthToken()) return

    inFlightRef.current = true
    try {
      const response = await refreshMutator.mutateAsync()
      setSessionToken(response.token)
      lastRefreshAtRef.current = Date.now()
    } catch (error: unknown) {
      const code =
        typeof error === 'object' &&
        error !== null &&
        'data' in error &&
        typeof (error as { data?: unknown }).data === 'object' &&
        (error as { data?: { code?: string } }).data?.code

      if (code === 'UNAUTHORIZED') {
        removeSessionToken()
        redirectToLogin()
      }
    } finally {
      inFlightRef.current = false
    }
  }, [refreshMutator.mutateAsync])

  useEffect(() => {
    void refreshSession()

    const intervalId = window.setInterval(() => {
      void refreshSession()
    }, SESSION_REFRESH_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      const now = Date.now()
      if (now - lastRefreshAtRef.current < SESSION_REFRESH_FOCUS_COOLDOWN_MS) return
      void refreshSession()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [refreshSession])
}
